import React, { useState, useRef, useEffect } from 'react';
import api from '../api';
import { IconIssy, IconVoice } from './Icons';

export default function IssyBanner({ context }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([
    { role: 'issy', text: "Hi! I'm Issy, your inventory AI. Ask me anything about your stock levels, what's low, or what to order." }
  ]);
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  async function ask(text) {
    if (!text.trim()) return;
    const userMsg = { role: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setQuery('');
    setLoading(true);
    try {
      const { data } = await api.post('/webhooks/voice/query', { query: text, context: context || 'inventory' });
      setMessages(prev => [...prev, { role: 'issy', text: data.response || data.answer || 'I checked your inventory — everything looks good!' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'issy', text: "Sorry, I couldn't reach the server right now. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  }

  function startVoice() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const rec = new SpeechRecognition();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      ask(transcript);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(query); }
  }

  return (
    <div className="issy-banner">
      <div className="issy-banner-left">
        <div className="issy-avatar">
          <IconIssy width={28} height={28} stroke="#00c9a7" />
        </div>
        <div>
          <div className="issy-name">Issy <span className="issy-tag">AI Assistant</span></div>
          <div className="issy-tagline">Ask me about inventory, stock levels, or what to order next</div>
        </div>
      </div>
      <button className="btn issy-cta" onClick={() => setOpen(o => !o)}>
        {open ? 'Close' : 'Ask Issy'}
      </button>

      {open && (
        <div className="issy-chat">
          <div className="issy-messages">
            {messages.map((m, i) => (
              <div key={i} className={`issy-msg issy-msg-${m.role}`}>
                {m.role === 'issy' && <span className="issy-msg-avatar"><IconIssy width={14} height={14} stroke="#00c9a7" /></span>}
                <span className="issy-msg-text">{m.text}</span>
              </div>
            ))}
            {loading && (
              <div className="issy-msg issy-msg-issy">
                <span className="issy-msg-avatar"><IconIssy width={14} height={14} stroke="#00c9a7" /></span>
                <span className="issy-msg-text issy-typing">Thinking…</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="issy-input-row">
            <input
              className="issy-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about stock, par levels, orders…"
              disabled={loading}
            />
            <button
              className={`btn issy-voice-btn${listening ? ' listening' : ''}`}
              onClick={startVoice}
              title="Speak to Issy"
              disabled={loading}
            >
              <IconVoice width={16} height={16} />
            </button>
            <button className="btn issy-send-btn" onClick={() => ask(query)} disabled={loading || !query.trim()}>
              Ask
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
